import type { ReactElement, ReactNode, VFC } from "react";
import { FormattedMessage } from "react-intl";

import { UnreadBadge } from "../UnreadBadge";

import {
  contentStyle,
  emptyStyle,
  iconStyle,
  messageStyle,
  notificationKindEmojiStyle,
  notificationKindStyle,
  rootStyle,
  startAtStyle,
  titleStyle,
} from "./index.styles";

import { Link } from "~/client/components/Link";
import { ConditionalWrapper } from "~/client/components/shared/ConditionalWrapper";
import { Timestamp } from "~/client/components/shared/Timestamp";
import { emojiMap } from "~/client/features/emoji";
import { ImageBrandKibelo } from "~/client/generated/assets";
import { ServiceNotificationKind } from "~/client/generated/schema";

const renderKind = (kind: ServiceNotificationKind): ReactNode => {
  let src = "";
  switch (kind) {
    case ServiceNotificationKind.NewFeature:
      src = emojiMap[":sparkles:"];
      break;
    case ServiceNotificationKind.Marketing:
      src = emojiMap[":rocket:"];
      break;
    case ServiceNotificationKind.Success:
      src = emojiMap[":bulb:"];
      break;
    case ServiceNotificationKind.Maintenance:
      src = emojiMap[":warning:"];
      break;
    case ServiceNotificationKind.Guide:
      return (
        <div css={notificationKindStyle}>
          <ImageBrandKibelo width="100%" height="100%" />
        </div>
      );
  }

  return (
    <div className="unicode-emoji" css={notificationKindEmojiStyle}>
      {src}
    </div>
  );
};

const renderLink = (url: string) => {
  return (children: ReactNode): ReactElement => (
    <Link to={url} className={"serviceNotificationItem__link ga-serviceNotificationItemLink"} target="_blank">
      {children}
    </Link>
  );
};

type ServiceNotification = {
  id: string;
  title: string;
  message: string;
  url?: string | null;
  notificationKind: ServiceNotificationKind;
  read: boolean;
  startAt: any;
};

type Props =
  | { empty: true }
  | {
      empty: false;
      serviceNotification: ServiceNotification;
    };

export const ServiceNotificationItem: VFC<Props> = (props) => {
  if (props.empty) {
    return (
      <li css={emptyStyle}>
        <FormattedMessage id="serviceNotifications.messages.empty" />
      </li>
    );
  }

  const { id, title, message, url, startAt, read, notificationKind } = props.serviceNotification;

  return (
    <li css={rootStyle} data-ga-track-id={id}>
      <ConditionalWrapper condition={url != undefined && url !== ""} node={renderLink(url!)}>
        <div css={contentStyle}>
          <div css={iconStyle}>
            <UnreadBadge unread={!read} />
            {renderKind(notificationKind)}
          </div>
          <h4 className="ga-serviceNotificationItemTitle" css={titleStyle}>
            {title}
          </h4>
          <p css={messageStyle}>{message}</p>
          <p css={startAtStyle}>
            <Timestamp value={startAt} />
          </p>
        </div>
      </ConditionalWrapper>
    </li>
  );
};
